<template>
    <v-container fluid class="pa-4">
        <v-row class="mb-2">
            <v-col cols="12" class="d-flex flex-wrap align-center justify-space-between ga-2">
                <div>
                    <h1 class="text-h5 font-weight-bold">Inspection Checklists</h1>
                    <p class="text-body-2 text-medium-emphasis mt-1">What drivers check on a pre/post-trip inspection</p>
                </div>
                <v-btn v-if="templates.length" color="primary" prepend-icon="mdi-plus" @click="openEditor()">New checklist</v-btn>
            </v-col>
        </v-row>

        <v-row v-if="loading && !templates.length">
            <v-col v-for="n in 2" :key="n" cols="12" md="6">
                <v-skeleton-loader type="article" elevation="2"/>
            </v-col>
        </v-row>

        <!-- No checklist yet: start from a ready-made one -->
        <template v-else-if="!templates.length">
            <p class="text-body-1 mb-4">
                Drivers can't inspect a vehicle until a checklist exists. Start from one of these, then change any item.
            </p>
            <v-row>
                <v-col v-for="p in PRESETS" :key="p.value" cols="12" md="6">
                    <v-card elevation="2" class="pa-5 h-100 d-flex flex-column">
                        <div class="d-flex align-center ga-3 mb-2">
                            <v-avatar color="primary" variant="tonal" size="48">
                                <v-icon size="28">{{ p.icon }}</v-icon>
                            </v-avatar>
                            <div>
                                <h2 class="text-h6">{{ p.title }}</h2>
                                <div class="text-body-2 text-medium-emphasis">{{ p.subtitle }}</div>
                            </div>
                        </div>
                        <p class="text-body-2 mb-4">{{ p.items }}</p>
                        <v-spacer/>
                        <v-btn color="primary" variant="flat" :loading="seeding === p.value" :disabled="!!seeding"
                               @click="seedDefault(p.value)">Use this checklist</v-btn>
                    </v-card>
                </v-col>
            </v-row>
            <div class="mt-4">
                <v-btn variant="text" color="primary" prepend-icon="mdi-plus" @click="openEditor()">Start from scratch</v-btn>
            </div>
        </template>

        <v-row v-else>
            <v-col v-for="t in templates" :key="t.id" cols="12" md="6">
                <v-card elevation="2" class="pa-4">
                    <div class="d-flex justify-space-between align-center">
                        <div>
                            <span class="font-weight-bold">{{ t.name }}</span>
                            <v-chip v-if="t.is_default" size="x-small" color="primary" variant="tonal" class="ml-1">Default</v-chip>
                        </div>
                        <div class="d-flex">
                            <v-btn icon="mdi-pencil" size="small" variant="text" :aria-label="`Edit ${t.name}`"
                                   @click="openEditor(t)"/>
                            <v-btn icon="mdi-content-copy" size="small" variant="text" :aria-label="`Duplicate ${t.name}`"
                                   @click="duplicate(t)"/>
                            <v-btn icon="mdi-delete" size="small" variant="text" color="error" :aria-label="`Delete ${t.name}`"
                                   @click="confirmRemove(t)"/>
                        </div>
                    </div>
                    <div class="text-caption text-medium-emphasis">
                        {{ (t.items || []).length }} items<span v-if="criticalCount(t)">, {{ criticalCount(t) }} critical</span>
                    </div>
                    <div class="d-flex flex-wrap ga-1 mt-2">
                        <v-chip v-for="i in (t.items || []).slice(0, 8)" :key="i.id" size="x-small" variant="outlined"
                                :color="i.is_critical ? 'error' : undefined">{{ i.label }}</v-chip>
                        <v-chip v-if="(t.items || []).length > 8" size="x-small" variant="text">
                            +{{ t.items.length - 8 }} more
                        </v-chip>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Editor -->
        <v-dialog v-model="editorDialog" max-width="820" scrollable>
            <v-card>
                <v-card-title class="d-flex justify-space-between align-center">
                    <span>{{ form.id ? 'Edit checklist' : 'New checklist' }}</span>
                    <v-btn icon="mdi-close" variant="text" size="small" aria-label="Close" @click="editorDialog = false"/>
                </v-card-title>
                <v-card-text>
                    <v-text-field v-model="form.name" label="Checklist name" variant="outlined" density="comfortable"
                                  class="mb-2" :rules="[v => !!v || 'Required']"/>
                    <v-switch v-model="form.is_default" color="primary" label="Default checklist (used when no other matches)"
                              hide-details density="compact" class="mb-2"/>

                    <div class="d-flex justify-space-between align-center mb-1">
                        <span class="text-subtitle-2 font-weight-bold">Items</span>
                        <v-btn size="small" variant="tonal" prepend-icon="mdi-plus" @click="addItem">Add item</v-btn>
                    </div>
                    <v-table density="compact">
                        <thead>
                        <tr>
                            <th style="width:72px"><span class="d-sr-only">Order</span></th>
                            <th>Item</th>
                            <th style="width:170px">Category</th>
                            <th class="text-center" style="width:80px">Critical</th>
                            <th class="text-center" style="width:80px">Photo</th>
                            <th style="width:40px"><span class="d-sr-only">Remove</span></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(it, i) in form.items" :key="it.key">
                            <td class="px-1 text-no-wrap">
                                <v-btn icon="mdi-arrow-up" size="x-small" variant="text" :disabled="i === 0"
                                       :aria-label="`Move ${it.label || 'item'} up`" @click="move(i, -1)"/>
                                <v-btn icon="mdi-arrow-down" size="x-small" variant="text" :disabled="i === form.items.length - 1"
                                       :aria-label="`Move ${it.label || 'item'} down`" @click="move(i, 1)"/>
                            </td>
                            <td><v-text-field v-model="it.label" variant="plain" density="compact" hide-details
                                              placeholder="e.g. Brakes" aria-label="Item"/></td>
                            <td><v-combobox v-model="it.category" :items="categories" variant="plain" density="compact"
                                            hide-details placeholder="optional" aria-label="Category"/></td>
                            <td class="text-center"><v-checkbox v-model="it.is_critical" hide-details density="compact"
                                                                color="error" aria-label="Critical"/></td>
                            <td class="text-center"><v-checkbox v-model="it.requires_photo" hide-details density="compact"
                                                                aria-label="Photo required"/></td>
                            <td><v-btn icon="mdi-close" size="x-small" variant="text" :aria-label="`Remove ${it.label || 'item'}`"
                                       @click="form.items.splice(i, 1)"/></td>
                        </tr>
                        </tbody>
                    </v-table>
                    <div v-if="!form.items.length" class="text-caption text-medium-emphasis py-2 text-center">Add at least one item.</div>
                    <p class="text-caption text-medium-emphasis mt-2">
                        <strong>Critical</strong> = a defect makes the vehicle unsafe (fails the inspection).
                        <strong>Photo</strong> = a photo is required when the item is flagged.
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn variant="text" @click="editorDialog = false">Cancel</v-btn>
                    <v-btn color="primary" :loading="saving" :disabled="!canSave" @click="save">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete confirmation -->
        <v-dialog v-model="deleteDialog" max-width="440">
            <v-card>
                <v-card-title class="text-h6">Delete checklist?</v-card-title>
                <v-card-text>
                    "{{ deleting?.name }}" will no longer be offered to drivers. Inspections already submitted keep their own copy.
                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn variant="text" :disabled="removing" @click="deleteDialog = false">Cancel</v-btn>
                    <v-btn color="error" variant="flat" :loading="removing" @click="remove">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">{{ snackbarMsg }}</v-snackbar>
    </v-container>
</template>

<script setup>
import {ref, reactive, computed, onMounted} from 'vue'
import {send} from '@/composables/apiClient'

const PRESETS = [
    {
        value: 'car_van',
        title: 'Car / van checklist',
        subtitle: 'Cars, pickups and vans',
        icon: 'mdi-van-utility',
        items: 'Brakes, lights, tires, seat belts, dash warning lights, fluid levels and leaks, wipers, mirrors, body damage and safety kit.',
    },
    {
        value: 'truck',
        title: 'Truck checklist (FMCSA 396.11)',
        subtitle: 'Commercial motor vehicles',
        icon: 'mdi-truck',
        items: 'The items FMCSA 49 CFR 396.11 lists: service and parking brakes, steering, lights and reflectors, tires, wheels and rims, horn, wipers, mirrors, coupling devices and emergency equipment.',
    },
]

const templates = ref([])
const loading = ref(false)
const seeding = ref('')
const saving = ref(false)
const editorDialog = ref(false)
const snackbar = ref(false)
const snackbarMsg = ref('')
const snackbarColor = ref('success')

let itemKey = 0
const newItem = (i = {}) => ({
    key: ++itemKey,
    label: i.label || '',
    category: i.category || '',
    is_critical: !!i.is_critical,
    requires_photo: !!i.requires_photo,
})

const form = reactive({id: null, name: '', is_default: false, asset_type_id: null, items: []})

const canSave = computed(() =>
    !!form.name.trim() && form.items.length > 0 && form.items.every(i => (i.label || '').trim())
)

// Categories already in use, offered in the editor so the same name is reused.
const categories = computed(() => {
    const all = [...templates.value.flatMap(t => t.items || []), ...form.items]
        .map(i => (i.category || '').trim())
        .filter(Boolean)
    return [...new Set(all)].sort((a, b) => a.localeCompare(b))
})

const criticalCount = (t) => (t.items || []).filter(i => i.is_critical).length

const flash = (msg, color = 'success') => {
    snackbarMsg.value = msg
    snackbarColor.value = color
    snackbar.value = true
}

const load = async () => {
    loading.value = true
    try {
        const res = await send('get', 'fleet/inspection-templates')
        templates.value = res?.data?.data ?? []
    } catch (e) {
        flash('Could not load checklists', 'error')
    } finally {
        loading.value = false
    }
}

const sortedItems = (t) => (t.items || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

const openEditor = (t = null) => {
    if (t) {
        form.id = t.id
        form.name = t.name
        form.is_default = !!t.is_default
        form.asset_type_id = t.asset_type_id ?? null
        form.items = sortedItems(t).map(newItem)
    } else {
        form.id = null
        form.name = ''
        form.is_default = templates.value.length === 0
        form.asset_type_id = null
        form.items = [newItem()]
    }
    editorDialog.value = true
}

// A copy opens in the editor and is only created on Save.
const duplicate = (t) => {
    openEditor(t)
    form.id = null
    form.name = `Copy of ${t.name}`.slice(0, 255)
    form.is_default = false
}

const addItem = () => form.items.push(newItem())

const move = (i, step) => {
    const j = i + step
    if (j < 0 || j >= form.items.length) return
    const [it] = form.items.splice(i, 1)
    form.items.splice(j, 0, it)
}

const save = async () => {
    if (!canSave.value) return
    saving.value = true
    const payload = {
        name: form.name.trim(),
        is_default: form.is_default,
        asset_type_id: form.asset_type_id,
        items: form.items.map((i, idx) => ({
            label: i.label.trim(), category: (i.category || '').trim() || null,
            is_critical: !!i.is_critical, requires_photo: !!i.requires_photo, sort_order: idx,
        })),
    }
    try {
        if (form.id) {
            await send('put', `fleet/inspection-templates/${form.id}`, payload)
        } else {
            await send('post', 'fleet/inspection-templates', payload)
        }
        editorDialog.value = false
        flash('Checklist saved')
        await load()
    } catch (e) {
        flash(e?.response?.data?.message || 'Could not save', 'error')
    } finally {
        saving.value = false
    }
}

const seedDefault = async (preset) => {
    seeding.value = preset
    try {
        await send('post', 'fleet/inspection-templates/seed-default', {preset})
        flash('Checklist created')
        await load()
    } catch (e) {
        flash(e?.response?.data?.message || 'Could not create', 'error')
    } finally {
        seeding.value = ''
    }
}

const deleteDialog = ref(false)
const deleting = ref(null)
const removing = ref(false)
const confirmRemove = (t) => {
    deleting.value = t
    deleteDialog.value = true
}
const remove = async () => {
    const t = deleting.value
    if (!t) return
    removing.value = true
    try {
        await send('delete', `fleet/inspection-templates/${t.id}`)
        templates.value = templates.value.filter(x => x.id !== t.id)
        deleteDialog.value = false
        flash('Checklist deleted')
    } catch (e) {
        flash('Could not delete', 'error')
    } finally {
        removing.value = false
    }
}

onMounted(load)
</script>
